import React from 'react';
import styled from '@emotion/styled';
import type { StayFull } from '../../../../interfaces/stays';
import { SCALE } from '../../../../motion';
import { touch } from '../../../../style-utils/media-queries';
import { duration, easing, layout, palette, radius, shadow, spacing, typography } from '../../../../style-utils/theme';

/**
 * THE HOST'S QUICK-CONTACT LINKS (plan S4, R-S4.36). Lobbero sends nothing:
 * "WhatsApp" opens wa.me in a new tab and "Call" opens the phone, both on the
 * host's own device. The hrefs are the server's (`contact`, stays:manage only),
 * so a session without stays:manage gets no links at all.
 *
 * Links, not buttons — they navigate. AppButton is a <button> with no `href`,
 * so this is AppButton's ghost/compact look on an anchor (same tokens, the
 * same press grammar, 44px on touch).
 */

const Row = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
  min-width: 0;
`;

const ContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${layout.compactControlHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid transparent;
  border-radius: ${radius.sm};
  background: transparent;
  color: ${palette.lagoon};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  transition: color ${duration.fast} ${easing.standard},
    background-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  &:hover {
    background: ${palette.tealTintSoft};
    box-shadow: ${shadow.sm};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }
`;

export interface ContactLinksProps {
  contact: StayFull['contact'];
  /** "Ana Silva" — every link's accessible name carries the guest. */
  name: string;
}

export const ContactLinks: React.FC<ContactLinksProps> = ({ contact, name }) => {
  if (!contact || (!contact.whatsapp && !contact.call)) return null;

  return (
    <Row>
      {contact.whatsapp ? (
        <ContactLink href={contact.whatsapp} target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${name}`}>
          WhatsApp
        </ContactLink>
      ) : null}
      {contact.call ? (
        <ContactLink href={contact.call} aria-label={`Call ${name}`}>
          Call
        </ContactLink>
      ) : null}
    </Row>
  );
};
